import React, { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import Routes from "./Routes"
import { LoadingToast } from "./components/LoadingToast.jsx"
import { CreditsTopUpOverlay } from "./components/ai/CreditsTopUpOverlay.jsx"
import { AIStateProvider } from "./components/providers/AIStateProvider.jsx"
import {
	defaultGlobalState,
	GlobalStateContext,
} from "./components/providers/GlobalStateProvider"
import { ProtectStateProvider } from "./components/providers/ProtectStateProvider.jsx"
import { useAuthenticatedFetch } from "./hooks/useAuthenticatedFetch"
import { useGQL } from "./hooks/useGQL"
import packageJSON from "./package.json"
import { appendScripts } from "./utils/appendScripts"
import { countVisit } from "./utils/countVisit.js"
import { InvalidAccessTokenOverlay } from "./components/InvalidAccessTokenOverlay.jsx"
import { BackgroundJobsStateProvider } from "./components/providers/BackgroundJobsStateProvider.jsx"
import { CRXModalProvider } from "./components/providers/CRXModalProvider.jsx"
import { useGetThemes } from "./hooks/useGetThemes.js"
import { GlobalBanner } from "./components/GlobalBanner/GlobalBanner.jsx"

export const TESTING_USER_ID_WHITELIST = [
	"6wDH7pGICtMu913QDkCaiU2cfwc2", // ***encia98@gmail.com
	"2GqNagAHIoSqW2G011ZN5qMyfFl1", // ***ale9@gmail.com
	"Ck5IOTAylThpxUlzC9beIlrYYnv1", // ***nixcom@gmail.com
	"ES7JSk7b8parDfA6OlocV96RQBy1", // ***iamangali@gmail.com
]

export const App = () => {
	const { state, dispatch } = useContext(GlobalStateContext)

	const location = useLocation()
	const navigate = useNavigate()
	const authenticatedFetch = useAuthenticatedFetch()
	const gqlQuery = useGQL()
	const { t } = useTranslation()
	const getThemes = useGetThemes()

	const [accessTokenIsInvalid, setAccessTokenIsInvalid] = useState(false)
	const [showInvalidAccessTokenOverlay, setShowInvalidAccessTokenOverlay] =
		useState(false)

	const pages = import.meta.glob("./pages/**/!(*.test.[jt]sx)*.([jt]sx)", {
		eager: true,
	})

	const getUserDataFromFirestore = async (shop) => {
		if (shop) {
			await authenticatedFetch(`/api/firestore/getUserData/${shop}`)
				.then((res) => res.json())
				.then((res) => {
					if (res.error) {
						console.error("getUserData error", res.error)
					} else {
						dispatch({
							hasTutorialDismissed:
								res.hasTutorialDismissed ??
								defaultGlobalState.hasTutorialDismissed,
							hasImportedAProduct:
								res.hasImportedAProduct ??
								defaultGlobalState.hasImportedAProduct,
							hasDismissedReviewBanner:
								res.hasDismissedReviewBanner ??
								defaultGlobalState.hasDismissedReviewBanner,
							hasDismissedAdBannerBlokyWide:
								res.hasDismissedAdBannerBlokyWide ??
								defaultGlobalState.hasDismissedAdBannerBlokyWide,
							hasDismissedAdBannerJustupsellWide:
								res.hasDismissedAdBannerJustupsellWide ??
								defaultGlobalState.hasDismissedAdBannerJustupsellWide,
							hasReadLatestChanges:
								res.readChanges?.[packageJSON.version] &&
								res.readChanges[packageJSON.version] === true,
							hasClosedAdDrawer:
								res.hasClosedAdDrawer ??
								defaultGlobalState.hasClosedAdDrawer,
							defaultTags:
								res.defaultTags ??
								defaultGlobalState.defaultTags,
							publishByDefault:
								res.publishByDefault ??
								defaultGlobalState.publishByDefault,
							statusActiveByDefault:
								res.statusActiveByDefault ??
								defaultGlobalState.statusActiveByDefault,
							roundingEnabledByDefault:
								res.roundingEnabledByDefault ??
								defaultGlobalState.roundingEnabledByDefault,
							roundingNumberByDefault:
								res.roundingNumberByDefault ??
								defaultGlobalState.roundingNumberByDefault,
							defaultVendor:
								res.defaultVendor ??
								defaultGlobalState.defaultVendor,
							taxableByDefault:
								res.taxableByDefault ??
								defaultGlobalState.taxableByDefault,
							skipDuplicates:
								res.skipDuplicates ??
								defaultGlobalState.skipDuplicates,
							disableCurrencyConversion:
								res.disableCurrencyConversion ??
								defaultGlobalState.disableCurrencyConversion,
							readChanges: res.readChanges ?? null,
							defaultTranslateLanguage:
								res.defaultTranslateLanguage ??
								defaultGlobalState.defaultTranslateLanguage,
							hasClosedFeedback:
								res.hasClosedFeedback ??
								defaultGlobalState.hasClosedFeedback,
							hasDismissedReimportTemplatesBanner:
								res.hasDismissedReimportTemplatesBanner ??
								defaultGlobalState.hasDismissedReimportTemplatesBanner,
							hasDismissedKopyPlusCTA:
								res.hasDismissedKopyPlusCTA ??
								defaultGlobalState.hasDismissedKopyPlusCTA,
							hasClosedScrollingSwapPromo:
								res.hasClosedScrollingSwapPromo ??
								defaultGlobalState.hasClosedScrollingSwapPromo,
						})
					}
				})
				.catch((err) => {
					console.error("getUserData catched error", err)
				})
		}
	}

	const getShopifyStoreDataFromGql = async () => {
		// Example for multiple queries in one, for future reference (shop locale)
		const { data, errors } = await gqlQuery(`
			query {
				ShopCurrency: shop {
					currencyCode
				}
				AppInstallation: currentAppInstallation {
					app {
						id
					}
					accessScopes {
						handle
					}
				}
			}
		`)

		if (data?.ShopCurrency?.currencyCode) {
			dispatch({
				shopCurrency: data.ShopCurrency.currencyCode,
				isCurrencyFetched: true,
			})
		}

		if (data?.AppInstallation?.app?.id) {
			dispatch({
				appId: /\d+/.exec(data.AppInstallation.app.id)?.[0],
			})
		}

		if (data?.AppInstallation?.accessScopes?.length) {
			dispatch({
				currentScopes: data.AppInstallation.accessScopes.map(
					(scope) => scope.handle,
				),
			})
		}

		if (errors) {
			console.error("getShopAndCurrencyFromGql errors", errors)

			if (errors[0]?.message?.includes("Unauthorized")) {
				setAccessTokenIsInvalid(true)
			}
		}

		// Requires additional read_locales scope
		const { data: shopLocalesData } = await gqlQuery(`
			query {
				ShopLocales: shopLocales {
					locale
					primary
					published
				}
			}
		`)

		if (shopLocalesData?.ShopLocales?.length) {
			let storeLanguage = null
			const availableStoreLanguages = shopLocalesData.ShopLocales.map(
				(locale) => {
					if (locale.primary) {
						storeLanguage = locale.locale
					}
					return locale.locale
				},
			)

			dispatch({
				availableStoreLanguages,
				storeLanguage,
			})
		}
	}

	const attachKcrxMessageListener = () => {
		window.addEventListener("message", (message) => {
			const { importType, importUrl, importFrom } = message.data

			if (importType && importUrl && importFrom) {
				if (importType === "product") {
					navigate(
						"/single?importUrl=" +
							importUrl +
							"&importFrom=" +
							importFrom,
					)
				} else {
					navigate(
						"/multiple?importUrl=" +
							importUrl +
							"&importFrom=" +
							importFrom,
					)
				}
			}
		})
	}

	const onAccessTokenUpdated = () => {
		setAccessTokenIsInvalid(false)
		setShowInvalidAccessTokenOverlay(false)
	}

	useEffect(async () => {
		const shop = state.shop
		appendScripts(shop)

		if (shop && state.accessToken) {
			countVisit()
			attachKcrxMessageListener()

			await Promise.all([
				getShopifyStoreDataFromGql(),
				getUserDataFromFirestore(shop),
				getThemes(),
			]).catch((err) => {
				console.error(err)
			})

			dispatch({ loadingUserData: false })
		} else if (state.savedStores.includes(shop)) {
			dispatch({ loadingUserData: false })
		}
	}, [])

	useEffect(() => {
		if (
			accessTokenIsInvalid &&
			["/", "/single", "/multiple", "/protect"].includes(
				location.pathname,
			)
		) {
			setShowInvalidAccessTokenOverlay(true)
		} else {
			setShowInvalidAccessTokenOverlay(false)
		}
	}, [accessTokenIsInvalid, location])

	const loading = state.loadingUserData

	if (loading) {
		document.querySelector("#app").style.pointerEvents = "none"
	} else {
		document.querySelector("#app").style.pointerEvents = "all"
	}

	return (
		<>
			<ProtectStateProvider>
				<AIStateProvider>
					<BackgroundJobsStateProvider>
						<CRXModalProvider>
							<GlobalBanner />
							<Routes pages={pages} />

							{loading && (
								<LoadingToast>
									{t("common.loading_store_settings")}
								</LoadingToast>
							)}

							<CreditsTopUpOverlay />

							{showInvalidAccessTokenOverlay && (
								<InvalidAccessTokenOverlay
									onAccessTokenUpdated={onAccessTokenUpdated}
								/>
							)}
						</CRXModalProvider>
					</BackgroundJobsStateProvider>
				</AIStateProvider>
			</ProtectStateProvider>
		</>
	)
}
